import jsPDF from 'jspdf';
import { TestResults } from '../../../../types';
import { PDF_COLORS } from '../../config/colors';
import { PDF_FONTS, PDF_LAYOUT } from '../../config/fonts';
import { PageManager } from '../../helpers/pageBreaks';

export class ResourcesSupportComponent {
  private pdf: jsPDF;
  private pageManager: PageManager;

  constructor(pdf: jsPDF, pageManager: PageManager) {
    this.pdf = pdf;
    this.pageManager = pageManager;
  }

  render(results: TestResults): void {
    this.renderSectionHeader();
    this.renderResourceCategories();
    this.renderSupportContacts();
  }

  private renderSectionHeader(): void {
    this.pageManager.checkForPageBreak(60, {
      minSpaceAfter: 40,
      elementType: 'resources-section-header',
    });

    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.subheading);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    this.pdf.text(
      'Resources & Support',
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY()
    );

    this.pageManager.addElement(PDF_FONTS.size.subheading, {
      spacingAfter: PDF_LAYOUT.spacing.paragraph,
      elementType: 'resources-header',
    });

    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.text.secondary);

    const description = this.pdf.splitTextToSize(
      'Access these resources and support services to accelerate your export readiness improvement journey.',
      this.pageManager.getContentWidth()
    );

    description.forEach((line: string) => {
      this.pdf.text(line, PDF_LAYOUT.margins.left, this.pageManager.getCurrentY());
      this.pageManager.addLine();
    });

    this.pageManager.addSection();
  }

  private renderResourceCategories(): void {
    const resourceCategories = [
      {
        title: 'Training & Education',
        resources: [
          'Lagos School of Export Management (LSEM) Training Programs',
          'Export Development Workshops and Seminars',
          'Online Export Readiness Courses',
          'Industry-Specific Export Training'
        ]
      },
      {
        title: 'Government Support',
        resources: [
          'Nigerian Export Promotion Council (NEPC)',
          'Nigerian Export-Import Bank (NEXIM)',
          'Small and Medium Enterprises Development Agency (SMEDAN)',
          'Export Development Fund Programs'
        ]
      },
      {
        title: 'Professional Services',
        resources: [
          'Export Consultancy Services',
          'International Trade Law Firms',
          'Freight Forwarding and Logistics Partners',
          'Market Research and Intelligence Services'
        ]
      },
      {
        title: 'Digital Tools & Platforms',
        resources: [
          'Export Documentation Software',
          'Trade Finance Platforms',
          'Market Intelligence Databases',
          'E-commerce and Digital Marketing Tools'
        ]
      }
    ];

    resourceCategories.forEach((category) => {
      this.renderResourceCategory(category);
    });
  }

  private renderResourceCategory(category: any): void {
    const categoryHeight = 40 + (category.resources.length * 8);
    this.pageManager.checkForPageBreak(categoryHeight, {
      minSpaceAfter: 20,
      elementType: 'resource-category',
    });

    // Category title
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.secondary);
    this.pdf.text(
      category.title,
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY()
    );

    this.pageManager.addElement(PDF_FONTS.size.body, {
      spacingAfter: PDF_LAYOUT.spacing.line,
      elementType: 'resource-category-title',
    });

    // Resources list
    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.text.secondary);

    category.resources.forEach((resource: string) => {
      const resourceLines = this.pdf.splitTextToSize(
        `• ${resource}`,
        this.pageManager.getContentWidth() - 10
      );

      resourceLines.forEach((line: string) => {
        this.pdf.text(line, PDF_LAYOUT.margins.left + 5, this.pageManager.getCurrentY());
        this.pageManager.addLine();
      });
    });

    this.pageManager.addParagraph();
  }

  private renderSupportContacts(): void {
    this.pageManager.checkForPageBreak(60, {
      minSpaceAfter: 30,
      elementType: 'support-contacts',
    });

    // Support section header
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    this.pdf.text(
      'Get Additional Support',
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY()
    );

    this.pageManager.addParagraph();

    // Contact information
    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.text.secondary);

    const contactInfo = [
      'For personalized export readiness consultation:',
      '',
      'Lagos School of Export Management (LSEM)',
      'Email: info@lsem.org.ng',
      'Website: www.lsem.org.ng',
      '',
      'Export Development Support:',
      'Nigerian Export Promotion Council (NEPC)',
      'Email: info@nepc.gov.ng',
      'Website: www.nepc.gov.ng'
    ];

    contactInfo.forEach((line) => {
      if (line === '') {
        this.pageManager.addLine();
      } else {
        this.pdf.text(line, PDF_LAYOUT.margins.left, this.pageManager.getCurrentY());
        this.pageManager.addLine();
      }
    });

    this.pageManager.addSection();

    // Final note
    this.pdf.setFillColor(...PDF_COLORS.background.primary);
    this.pdf.rect(
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY(),
      this.pageManager.getContentWidth(),
      25,
      'F'
    );

    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    this.pdf.text(
      'Remember: Export success is a journey, not a destination. Take it one step at a time!',
      PDF_LAYOUT.margins.left + this.pageManager.getContentWidth() / 2,
      this.pageManager.getCurrentY() + 15,
      { align: 'center' }
    );

    this.pageManager.addElement(30, {
      spacingAfter: 0,
      elementType: 'final-note',
    });
  }
}